import type { JSONSchemaType } from "ajv";

import { CanonicalDataType } from "@/shared/domain/topics";

import { type BaseSchema } from "../base";

import * as threeD from "./three-d";
import * as threeDSchema from "./three-d/three-d-jsonschema";
import { TopicData, topicDataSchema } from "./topic-data";

export interface File {
  fileId: string;
  relativePath?: string;
  associationId?: string;
}
const fileSchema: JSONSchemaType<File> = {
  type: "object",
  properties: {
    fileId: { type: "string" },
    relativePath: { type: "string", nullable: true },
    associationId: { type: "string", nullable: true },
  },
  required: ["fileId"],
  additionalProperties: false,
};

export interface Dataset {
  datasetId: string;
}
const datasetSchema: JSONSchemaType<Dataset> = {
  type: "object",
  properties: {
    datasetId: { type: "string" },
  },
  required: ["datasetId"],
  additionalProperties: false,
};

export interface DatasetSource {
  type: "dataset";
  value: Dataset;
}

export interface FileSource {
  type: "file";
  value: File;
}

export type Datasource = DatasetSource | FileSource;

const datasourceSchema: JSONSchemaType<Datasource> = {
  type: "object",
  oneOf: [
    {
      properties: {
        type: { type: "string", const: "file" },
        value: fileSchema,
      },
      required: ["type", "value"],
      additionalProperties: false,
    },
    {
      properties: {
        type: { type: "string", const: "dataset" },
        value: datasetSchema,
      },
      required: ["type", "value"],
      additionalProperties: false,
    },
  ],
  required: ["type", "value"],
};

export enum PanelType {
  Image = "Image",
  Log = "Log",
  Map = "Map",
  Plot = "Plot",
  RawMessage = "RawMessage",
  ThreeD = "ThreeD",
}

export enum LayoutType {
  Panel = "Panel",
}

export interface PanelState<
  Data = unknown,
  Config = unknown,
  InitParams = unknown,
> {
  config?: Config;
  data: Data;
  id: string;
  title: string;
  type: PanelType;
  initParams?: InitParams; // Initialization params captured at share-time
}

//// IMAGE PANEL
export interface ImagePanelClip {
  data: TopicData;
  id: string;
}

const imageClipSchema: JSONSchemaType<ImagePanelClip> = {
  type: "object",
  properties: {
    data: topicDataSchema,
    id: { type: "string" },
  },
  required: ["data", "id"],
  additionalProperties: true,
};

export interface ImagePanelConfig {
  rotation: number; // in degrees
  stretchToFitCanvas: boolean;
}
const imagePanelConfigSchema: JSONSchemaType<ImagePanelConfig> = {
  type: "object",
  properties: {
    rotation: { type: "number" },
    stretchToFitCanvas: { type: "boolean" },
  },
  required: ["rotation", "stretchToFitCanvas"],
  additionalProperties: true,
};

export interface ImagePanelState
  extends PanelState<ImagePanelClip[], ImagePanelConfig> {}
export function isImagePanelState(state: PanelState): state is ImagePanelState {
  return state.type === PanelType.Image;
}

//// MAP PANEL
export type CoordinateUnit = "deg_float" | "deg_int_1e7";

export interface MapPath {
  data: TopicData[];
  id: string;
  /**
   * Optional user-specified coordinate units, needed when a field has a generic
   * Number canonical type (e.g. ArduPilot GPS fields).
   */
  coordinateUnits?: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  };
  // This is a prop bag to store any style-related state.
  style: {
    lineColor?: string;
  };
  visible: boolean;
}
const mapPathSchema: JSONSchemaType<MapPath> = {
  type: "object",
  properties: {
    data: { type: "array", items: topicDataSchema },
    id: { type: "string" },
    coordinateUnits: {
      type: "object",
      nullable: true,
      properties: {
        lat: {
          type: "string",
          enum: ["deg_float", "deg_int_1e7"],
          nullable: true,
        },
        lon: {
          type: "string",
          enum: ["deg_float", "deg_int_1e7"],
          nullable: true,
        },
      },
      required: [],
    },
    style: {
      type: "object",
      properties: {
        lineColor: { type: "string", nullable: true },
      },
      required: [],
      additionalProperties: true,
    },
    visible: { type: "boolean" },
  },
  required: ["data", "id", "style", "visible"],
  additionalProperties: true,
};

export interface MapPanelInitParams {
  viewport?: {
    center: { lng: number; lat: number };
    zoom: number;
    bearing?: number;
    pitch?: number;
  };
}

export interface MapPanelState
  extends PanelState<MapPath[], undefined, MapPanelInitParams> {}
export function isMapPanelState(state: PanelState): state is MapPanelState {
  return state.type === PanelType.Map;
}

//// PLOT PANEL
export interface PlotSeriesConfig {
  lineColor: string;
  /**
   * Default defined in web-ui/src/shared/state/visualization/plotDefaults.ts.
   */
  lineWidth?: number;
  /**
   * Default defined in web-ui/src/shared/state/visualization/plotDefaults.ts.
   */
  pointRadius?: number;
  /**
   * The y axis to display the given series on in ChartJS.
   *
   * Defaults to 0 if unspecified. Should be a non-negative integer.
   *
   * See https://www.chartjs.org/docs/latest/samples/line/multi-axis.html
   * for more about how these work in ChartJS.
   */
  yAxis?: PlotYAxis;
  /**
   * When true, gaps are shown where discontinuities (null/NaN values) occur.
   * When false, lines are drawn between data segments even when there are discontinuities.
   *
   * Default defined in web-ui/src/shared/state/visualization/plotDefaults.ts.
   */
  breakLineAtDiscontinuity?: boolean;
  /**
   * An optional human-readable label for the series, shown in the legend
   * instead of the default message-path string.
   */
  label?: string;
  /**
   * An optional point-wise transform applied to each Y value before rendering.
   *
   * Only `type: "formula"` is supported.
   *
   * @example { type: "formula", expression: "y * 100" }
   */
  transform?: FormulaTransform;
}

/**
 * A point-wise math transform applied to each Y value in a series.
 * The expression is evaluated with `y` as the data-point value.
 */
export interface FormulaTransform {
  type: "formula";
  /** The math expression string, e.g. `"y * 100"` or `"(y - 32) * 5 / 9"`. */
  expression: string;
}

const formulaTransformSchema: JSONSchemaType<FormulaTransform> = {
  type: "object",
  properties: {
    type: { type: "string", const: "formula" },
    expression: { type: "string" },
  },
  required: ["type", "expression"],
};

export interface PlotSeries {
  data: TopicData;
  id: string;
  // This is a prop bag to store configurable state (such as style)
  config?: PlotSeriesConfig;
  visible: boolean;
}
const plotSeriesSchema: JSONSchemaType<PlotSeries> = {
  type: "object",
  properties: {
    config: {
      type: "object",
      properties: {
        lineColor: { type: "string" },
        lineWidth: { type: "number", nullable: true },
        pointRadius: { type: "number", nullable: true },
        yAxis: { type: "string", nullable: true },
        breakLineAtDiscontinuity: { type: "boolean", nullable: true },
        label: { type: "string", nullable: true },
        transform: {
          type: "object",
          nullable: true,
          oneOf: [formulaTransformSchema],
        },
      },
      required: ["lineColor"],
      additionalProperties: true,
      nullable: true,
    },
    data: topicDataSchema,
    id: { type: "string" },
    visible: { type: "boolean" },
  },
  required: ["data", "id", "visible"],
  additionalProperties: true,
};
/**
 * Which axes of the viewport of a particular panel to sync with other panels.
 *
 * "xy" - sync both axes
 * "x" - sync the x axis
 * "y" - sync the y axis
 * "" - sync nothing
 */
export type ViewportSyncMode = "xy" | "x" | "y" | "";

export interface PlotPanelInitParams {
  viewport?: {
    x: { min: number; max: number };
    y: { min: number; max: number };
  };
}

export interface PlotPanelState
  extends PanelState<PlotSeries[], PlotPanelConfig, PlotPanelInitParams> {}
export function isPlotPanelState(state: PanelState): state is PlotPanelState {
  return state.type === PanelType.Plot;
}

export enum LegendPosition {
  TopLeft = "TopLeft",
  TopRight = "TopRight",
  BottomLeft = "BottomLeft",
  BottomRight = "BottomRight",
}

export enum TimeBoundsSource {
  FilesInWorkspace = "FilesInWorkspace",
  FilesInPanel = "FilesInPanel",
  TopicsInPanel = "TopicsInPanel",
}

export interface PlotAxisConfig {
  label?: string;
  /**
   * The bounds of this axis of the chart.
   *
   * The y axes of a chart can be locked to fixed bounds.
   *
   * Bounds updates can originate either inside the chart by pan or zoom events,
   * or outside the chart by changing the chart settings.
   */
  bounds?: {
    min: number;
    max: number;
    locked?: boolean;
    setBy?: "settings" | "chart";
  };
}

const plotAxisConfigSchema: JSONSchemaType<PlotAxisConfig> = {
  type: "object",
  properties: {
    label: { type: "string", nullable: true },
    bounds: {
      type: "object",
      properties: {
        min: { type: "number", nullable: false },
        max: { type: "number", nullable: false },
        locked: { type: "boolean", nullable: true },
        setBy: { type: "string", enum: ["settings", "chart"], nullable: true },
      },
      required: ["min", "max"],
      nullable: true,
    },
  },
  required: [],
};

export interface PlotPanelConfig {
  timeBoundsSource: TimeBoundsSource;
  showEvents: boolean;
  viewportSyncMode?: ViewportSyncMode;
  legendPosition?: LegendPosition;
  // The plot panel is based on exploring time-series data projected across the x axis;
  // it should be free to sync with event timestamps, etc.
  xAxis?: PlotAxisConfig;
  yAxis?: PlotAxisConfig;
  extraYAxes?: Partial<Record<PlotYAxis, PlotAxisConfig>>;
}

export type PlotYAxis = "y" | `y${number}${"l" | "r"}`;

const plotPanelConfigSchema: JSONSchemaType<PlotPanelConfig> = {
  type: "object",
  properties: {
    timeBoundsSource: { type: "string" },
    showEvents: { type: "boolean" },
    viewportSyncMode: { type: "string", nullable: true },
    xAxis: { ...plotAxisConfigSchema, nullable: true },
    yAxis: { ...plotAxisConfigSchema, nullable: true },
    extraYAxes: {
      type: "object",
      required: [],
      items: {
        ...plotAxisConfigSchema,
        nullable: true,
      },
      nullable: true,
    },
    legendPosition: { type: "string", nullable: true },
  },
  required: [],
  additionalProperties: true,
};

//// 3D PANEL
export type {
  PanelData as ThreeDPanelData,
  PanelConfig as ThreeDPanelConfig,
  PanelInitParams as ThreeDPanelInitParams,
  Entity as ThreeDEntity,
} from "./three-d";

export interface ThreeDPanelState
  extends PanelState<
    threeD.PanelData,
    threeD.PanelConfig,
    threeD.PanelInitParams
  > {}
export function isThreeDPanelState(
  state: PanelState,
): state is ThreeDPanelState {
  return state.type === PanelType.ThreeD;
}

//// RAW MESSAGE PANEL
export interface RawMessagePanelState extends PanelState<TopicData | null> {}
export function isRawMessagePanelState(
  state: PanelState,
): state is RawMessagePanelState {
  return state.type === PanelType.RawMessage;
}

//// LOG PANEL
export interface LogPanelConfig {
  searchFilter?: {
    term: string;
    mode: "string" | "regex";
  };
  // Column filters: key is messagePathId, value is filter string
  columnFilters?: Record<string, string>;
}

const logPanelConfigSchema: JSONSchemaType<LogPanelConfig> = {
  type: "object",
  properties: {
    searchFilter: {
      type: "object",
      properties: {
        term: { type: "string" },
        mode: { type: "string", enum: ["string", "regex"] },
      },
      required: ["term", "mode"],
      nullable: true,
    },
    columnFilters: {
      type: "object",
      required: [],
      additionalProperties: { type: "string" },
      nullable: true,
    },
  },
  required: [],
  additionalProperties: true,
};

export interface LogPanelState
  extends PanelState<TopicData[], LogPanelConfig, undefined> {}
export function isLogPanelState(state: PanelState): state is LogPanelState {
  return state.type === PanelType.Log;
}

export type PanelStateByType<T extends PanelType = PanelType> = {
  [PanelType.Image]: ImagePanelState;
  [PanelType.Log]: LogPanelState;
  [PanelType.Map]: MapPanelState;
  [PanelType.Plot]: PlotPanelState;
  [PanelType.RawMessage]: RawMessagePanelState;
  [PanelType.ThreeD]: ThreeDPanelState;
}[T];

export type PanelStates =
  | ImagePanelState
  | LogPanelState
  | MapPanelState
  | PlotPanelState
  | RawMessagePanelState
  | ThreeDPanelState;

const panelSchema: JSONSchemaType<PanelStates> = {
  type: "object",
  anyOf: [
    {
      properties: {
        type: { const: PanelType.Plot },
        config: plotPanelConfigSchema,
        data: {
          type: "array",
          items: plotSeriesSchema,
        },
        id: { type: "string" },
        title: { type: "string" },
      },
    },
    {
      properties: {
        type: { const: PanelType.ThreeD },
        config: threeDSchema.panelConfigSchema,
        data: threeDSchema.panelDataSchema,
        id: { type: "string" },
        title: { type: "string" },
      },
    },
    {
      properties: {
        type: { const: PanelType.Map },
        data: {
          type: "array",
          items: mapPathSchema,
        },
        id: { type: "string" },
        title: { type: "string" },
      },
    },
    {
      properties: {
        type: { const: PanelType.RawMessage },
        data: topicDataSchema,
        id: { type: "string" },
        title: { type: "string" },
      },
    },
    {
      properties: {
        type: { const: PanelType.Image },
        config: imagePanelConfigSchema,
        data: { type: "array", items: imageClipSchema },
        id: { type: "string" },
        title: { type: "string" },
      },
    },
    {
      properties: {
        type: { const: PanelType.Log },
        config: logPanelConfigSchema,
        data: {
          type: "array",
          items: topicDataSchema,
        },
        id: { type: "string" },
        title: { type: "string" },
      },
    },
  ],
  required: ["data", "type", "id"],
  additionalProperties: true,
};

interface BaseLayout {
  id: string;
  relativeSize: number;
}

export interface LayoutItem extends BaseLayout {
  type: LayoutType;
  isResizing: boolean;
}

export interface Layout extends BaseLayout {
  axis: "x" | "y";
  children: (Layout | LayoutItem)[];
}

export function isLayoutItem(
  layout: Layout | LayoutItem,
): layout is LayoutItem {
  return "children" in layout === false;
}

const LayoutItemSchema: JSONSchemaType<LayoutItem> = {
  $id: "LayoutItemSchema",
  type: "object",
  properties: {
    id: {
      type: "string",
    },
    relativeSize: {
      type: "number",
    },
    isResizing: {
      type: "boolean",
    },
    type: {
      type: "string",
      enum: [LayoutType.Panel],
    },
  },
  required: ["id", "relativeSize", "isResizing", "type"],
};

const LayoutSchema: JSONSchemaType<Layout> = {
  $id: "LayoutSchema",
  type: "object",
  properties: {
    id: {
      type: "string",
    },
    axis: {
      type: "string",
      enum: ["x", "y"],
    },
    relativeSize: {
      type: "number",
    },
    children: {
      type: "array",
      items: {
        anyOf: [
          {
            type: "object",
            $ref: "LayoutSchema",
            required: ["id", "axis", "relativeSize", "children"],
          },
          LayoutItemSchema,
        ],
      },
    },
  },
  required: ["id", "axis", "relativeSize", "children"],
};

export interface EventConfig {
  isVisible: boolean;
}

const eventConfigSchema: JSONSchemaType<EventConfig> = {
  type: "object",
  properties: {
    isVisible: { type: "boolean" },
  },
  required: ["isVisible"],
  additionalProperties: true,
};

export interface State extends BaseSchema {
  /**
   * @deprecated Use datasources instead
   */
  files: File[];

  datasources?: Datasource[];

  // panel id -> panel state
  panels?: {
    [id: string]: PanelStates;
  };

  layout: Layout;

  // event id -> event config
  events?: {
    [id: string]: EventConfig;
  };
}

export const VERSION = "visualization_v1";

export const schema: JSONSchemaType<State> = {
  $id: VERSION,
  type: "object",
  properties: {
    version: { type: "string", const: VERSION },
    files: {
      type: "array",
      items: fileSchema,
    },
    datasources: {
      type: "array",
      items: datasourceSchema,
      nullable: true,
    },
    panels: {
      type: "object",
      nullable: true,
      required: [],
      patternProperties: {
        ".*": panelSchema,
      },
    },
    layout: LayoutSchema,
    events: {
      type: "object",
      nullable: true,
      required: [],
      patternProperties: {
        ".*": eventConfigSchema,
      },
    },
  },
  required: ["files", "layout"],
  additionalProperties: true,
};

/**
 * Make a default decision for how to visualize a data type.
 * E.g., a numeric type should be visualized as a plot,
 * a string should be visualized as a "raw message," etc.
 */
export function determineDefaultPanelType(
  canonicalDataType: CanonicalDataType,
): PanelType {
  switch (canonicalDataType) {
    case CanonicalDataType.Number:
    case CanonicalDataType.NumberArray:
    case CanonicalDataType.Boolean: // Fall through
      return PanelType.Plot;
    case CanonicalDataType.Image:
      return PanelType.Image;
    case CanonicalDataType.Categorical:
    case CanonicalDataType.String:
    case CanonicalDataType.Timestamp: // Fall through
      return PanelType.Log;
    case CanonicalDataType.LatDegFloat:
    case CanonicalDataType.LonDegFloat:
    case CanonicalDataType.LatDegInt:
    case CanonicalDataType.LonDegInt: // Fall through
      return PanelType.Map;
    default:
      return PanelType.RawMessage;
  }
}
